import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, type SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { platformAdminValidator, type ValidationResult } from '@/utils/platformAdminValidation';

// `src/integrations/supabase/types.ts` (generated) declares zero tables and a single
// function, so every real table or RPC name fails to type-check -- that is where most of
// the errors counted in `scripts/tipos-linea-base.json` come from. `db` is the same client,
// same session, same RLS, read through its untyped surface, so the calls below do not add
// to that pile while the generated file stays as it is.
const db = supabase as unknown as SupabaseClient;

interface PlatformAdminContextType {
  user: User | null;
  session: Session | null;
  isPlatformAdmin: boolean;
  /**
   * True when this session is a scoped READ-ONLY console session (minted by the
   * mint-scoped-session bridge, marked with permissions.access_level =
   * 'read_only'). The database refuses its writes regardless, so this exists so
   * the SCREEN can say so instead of letting the operator find out by having an
   * action fail (UX registry T-11).
   */
  isReadOnlySession: boolean;
  validationState: ValidationResult;
  loading: boolean;
  signOut: () => Promise<void>;
  checkPlatformAdminStatus: () => Promise<boolean>;
  refreshValidation: () => Promise<void>;
}

const PlatformAdminContext = createContext<PlatformAdminContextType | undefined>(undefined);

export function PlatformAdminProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [isReadOnlySession, setIsReadOnlySession] = useState(false);
  const [validationState, setValidationState] = useState<ValidationResult>('unknown');
  const [loading, setLoading] = useState(true);

  const checkPlatformAdminStatus = async (): Promise<boolean> => {
    const result = await platformAdminValidator.validatePlatformAdmin(user?.id);
    return result === 'valid';
  };

  const refreshValidation = async () => {
    if (user?.id) {
      platformAdminValidator.clearCache(user.id);
      const result = await platformAdminValidator.validatePlatformAdmin(user.id);
      setValidationState(result);
      setIsPlatformAdmin(result === 'valid');
    }
  };

  const verifyPlatformAdmin = async (userId: string) => {
    const adminStatus = await checkPlatformAdminStatus();
    setIsPlatformAdmin(adminStatus);
    return adminStatus;
  };


  // One read per session, not per screen: the flag lives on the admin row the
  // bridge writes when it provisions the dedicated viewer. A failure to read it
  // leaves the badge off, which is the honest default -- the database is still
  // the thing that refuses writes.
  const resolveReadOnly = async (userId: string, isAdmin: boolean) => {
    if (!isAdmin) { setIsReadOnlySession(false); return; }
    try {
      const { data } = await db
        .from('platform_admin_users')
        .select('permissions')
        .eq('user_id', userId)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      const level = (data?.permissions as Record<string, unknown> | null)?.access_level;
      setIsReadOnlySession(level === 'read_only');
    } catch {
      setIsReadOnlySession(false);
    }
  };

  const debugPlatformAdminStatus = (userId: string, result: ValidationResult, isPlatformAdmin: boolean) => {
    console.log('🔍 [PlatformAdminContext] Debug Info:', {
      userId,
      validationResult: result,
      isPlatformAdminState: isPlatformAdmin,
      currentPath: window.location.pathname,
      shouldUsePlatformAdminLayout: isPlatformAdmin
    });
  };

  useEffect(() => {
    // PlatformAdminContext: Initializing
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Ignore TOKEN_REFRESHED events to reduce noise
        if (event === 'TOKEN_REFRESHED') {
          return;
        }

        // PlatformAdminContext: Auth state changed
        setSession(session);
        setUser(session?.user ?? null);
        
        // Check platform admin status if we have a user
        if (session?.user) {
          setTimeout(async () => {
            // Clear any cached results first to force fresh validation
            platformAdminValidator.clearCache(session.user.id);
            
            const result = await platformAdminValidator.validatePlatformAdmin(session.user.id);
            const isPlatformAdminResult = result === 'valid';
            
            setValidationState(result);
            setIsPlatformAdmin(isPlatformAdminResult);
            void resolveReadOnly(session.user.id, isPlatformAdminResult);
            
            // Add debugging
            debugPlatformAdminStatus(session.user.id, result, isPlatformAdminResult);
            
            setLoading(false);
          }, 0);
        } else {
          setValidationState('invalid');
          setIsPlatformAdmin(false);
          setLoading(false);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      // PlatformAdminContext: Initial session check
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        setTimeout(async () => {
          // Clear any cached results first to force fresh validation
          platformAdminValidator.clearCache(session.user.id);
          
          const result = await platformAdminValidator.validatePlatformAdmin(session.user.id);
          const isPlatformAdminResult = result === 'valid';
          
          setValidationState(result);
          setIsPlatformAdmin(isPlatformAdminResult);
          
          // Add debugging
          debugPlatformAdminStatus(session.user.id, result, isPlatformAdminResult);
          
          setLoading(false);
        }, 0);
      } else {
        setValidationState('invalid');
        setIsPlatformAdmin(false);
        setLoading(false);
      }
    });

    return () => {
      // PlatformAdminContext: Cleaning up subscription
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      // Error signing out
    }
  };

  const value = {
    user,
    session,
    isPlatformAdmin,
    isReadOnlySession,
    validationState,
    loading,
    signOut,
    checkPlatformAdminStatus,
    refreshValidation
  };

  return (
    <PlatformAdminContext.Provider value={value}>
      {children}
    </PlatformAdminContext.Provider>
  );
}

export function usePlatformAdmin() {
  const context = useContext(PlatformAdminContext);
  if (context === undefined) {
    throw new Error('usePlatformAdmin must be used within a PlatformAdminProvider');
  }
  return context;
}